import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import {
	autoinject,
	computedFrom,
	InlineViewStrategy,
	observable,
	PLATFORM,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { NavigationInstruction, RouteConfig, Router } from 'aurelia-router';
import { BindingSignaler } from 'aurelia-templating-resources';

import { AtoInterfacesApi } from '~/api/ato-interfaces-api';
import { CustomisationsApi } from '~/api/customisations-api';
import { APIParameters, GenericApi } from '~/api/generic-api';
import {
	AcurityTable,
	AcurityTableConfig,
	AcurityTableRecord,
	DataTableRequestFilter,
} from '~/components/acurity-table/acurity-table';
import { customHighlight } from '~/components/acurity-table/acurity-table-utils';
import { ContextMenuItem } from '~/components/context-menu-list/context-menu-list';
import {
	ArchiveCheckbox,
	CustomisationConfig,
} from '~/components/customisation/customisation-config';
import { BaseDialog, DialogSize } from '~/components/dialog/base-dialog';
import {
	DialogWithTabs,
	DialogWithTabsModel,
} from '~/components/dialog/dialog-with-tabs/dialog-with-tabs';
import { DrilldownCommonModel } from '~/components/dialog/dialog-with-tabs/drilldown-model';
import { SimpleDialogType } from '~/components/dialog/simple-dialog';
import { Filter } from '~/components/filter/filter';
import { FilterType } from '~/components/filter/filter-options';
import { Notification } from '~/components/notification/notification';
import { CommonURLParam } from '~/constants';
import { PresentationType } from '~/converters/presentation-type';
// tslint:disable-next-line no-circular-imports
import { DataCompareTab } from '~/forms/ato-interfaces/drilldown-dialog/ato/data-compare-tab';
// tslint:disable-next-line no-circular-imports
import { TemplateMapping } from '~/forms/ato-interfaces/drilldown-dialog/template-mapping';
import { AtoInterfacesIcons } from '~/forms/ato-interfaces/icon-list/ato-interfaces-icons';
import {
	MaasGetDialog,
	MaasGetDialogModel,
} from '~/forms/ato-interfaces/maas-get-dialog';
import {
	AtoInterfacesConfig,
	AtoInterfacesConfigWrapper,
} from '~/forms/ato-interfaces/models/ato-interfaces-config';
import { ATOInterfacesData } from '~/forms/ato-interfaces/models/ato-interfaces-data';
import { ATOInterfacesModel } from '~/forms/ato-interfaces/models/ato-interfaces-model';
import { ATORequest } from '~/forms/ato-interfaces/models/ato-request';
import { RequestControlFilters } from '~/forms/ato-interfaces/models/request-control';
import {
	RequestStatus,
	RequestType,
} from '~/forms/ato-interfaces/models/request-status';
import {
	ResponseBean,
	RolloverMessage,
	SuperFund,
} from '~/forms/ato-interfaces/models/response-bean';
import {
	ExcelExportDialog,
	ExcelExportDialogModel,
} from '~/forms/excel-export-dialog';
import I18nForm from '~/forms/i18n-form';
import { CodeAndDescription } from '~/models/code-and-description';
import {
	ColumnDefinition,
	ColumnValue,
	HiddenTarget,
} from '~/models/column-definition';
import { DataTypeEnum } from '~/models/data-type';
import { createFilterModel, FilterModel } from '~/models/filter-model';
import { FieldModel } from '~/models/form-model';
import {
	HasArchiveCheckbox,
	HasCustomisationConfig,
} from '~/models/page-controller';
import {
	ArchiveCheckboxModel,
	getArchiveCheckboxModel,
	setArchiveQueryParam,
} from '~/utils/archive-utils';
import { BlockUtils } from '~/utils/block-utils';
import { Converters } from '~/utils/converters';
import { LocalizedDateTimeFormats } from '~/utils/datetime/localized-date-time-formats';
import { DialogUtils } from '~/utils/dialog-utils';
import { FetchUtils } from '~/utils/fetch-utils';
import { JQueryHelper } from '~/utils/jquery-helper';
import { Logger } from '~/utils/logger';

enum ATORequestType {
	STIC = 'STIC',
	SMAT = 'SMAT',
	MATS = 'MATS',
	MAAS_GET = 'MAAS-GET',
	MAAS_SUBMIT = 'MAAS-SUBMIT',
	MAAS_SUBMIT_FOUNDATION = 'MAAS-SUBMIT-FOUNDATION',
	SMSFVRFY = 'SMSFVRFY',
}

@autoinject
export class ATOInterfaces
	extends I18nForm
	implements HasArchiveCheckbox, HasCustomisationConfig
{
	public customisationConfig: CustomisationConfig;
	public atoInterfaceTable: AcurityTable;
	public viewStrategy: InlineViewStrategy;
	public atoInterfacesData: ATOInterfacesData;
	public canRefresh: boolean = false;
	public responseBean: ResponseBean;
	public filterSidebarOpen: boolean = false;
	public initialData: Promise<any>;
	public atoInterfaceFilter: Filter;
	public eventSubscriptions: Subscription[] = [];
	public static readonly FORM_NAME = 'ato-interfaces';

	protected tableConfig: AcurityTableConfig = {
		tableColumns: [],
		multiColumnOrder: [],
		secondarySortFields: [
			{ primary: 'atoRequest', secondary: 'atoRequest.TQz_STIC_Mode' },
			{
				primary: 'atoReceived',
				secondary: 'atoRequest.TQt_ATO_Received',
			},
		],
		rowIdKey: 'atoRequest.TQi_Identity',
		defaultSortField: 'atoRequest.TQi_Identity',
		deletedHighlightField: '',
		defaultSortDirection: 'desc',
	};

	private readonly releaseOnHoldMenuId: string = 'releaseOnHoldMenuId';
	private readonly markReviewedMenuId: string = 'markReviewedMenuId';
	private readonly responseReviewedMenuId: string = 'responseReviewedMenuId';
	private readonly resubmitMenuId: string = 'resubmitMenuId';
	private readonly resubmitBatchMenuId: string = 'resubmitBatchMenuId';

	private member: string;
	private fund: string;
	private division: string;
	private unfilteredMember: string;
	private unfilteredFund: string;

	private errorRequestStatuses = ['E', 'J', 'K'];
	private atoInterfacesConfig: AtoInterfacesConfig;

	// Fields which are not part of formmodel but are required as part of request
	private queryParams: Map<string, string> = new Map();
	public functionName: string;

	@observable public archiveCheckboxChecked: boolean = false;
	archiveCheckboxHidden: boolean = false;

	constructor(
		public api: AtoInterfacesApi,
		router: Router,
		private customisationsApi: CustomisationsApi,
		public i18n: I18N,
		bindingSignaler: BindingSignaler,
		eventAggregator: EventAggregator,
		element: Element,
		readonly dialogUtils: DialogUtils,
	) {
		super(router, i18n, bindingSignaler, eventAggregator, element);
		this.initEventHandlers();
		this.api = api;

		this.tableConfig.onDraw = this.onTableDraw;
		this.tableConfig.tableColumns = [
			{
				name: 'atoRequest.TQz_Fund',
				label: 'Fund',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: 'atoRequest.TQz_Member',
				label: 'Member',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: 'atoRequest.TQk_Created',
				label: 'Request Date',
				dataType: { name: DataTypeEnum.Timestamp },
			},
			{
				name: 'atoRequest.TQd_Effective',
				label: 'Effective Date',
				dataType: { name: DataTypeEnum.Date },
			},
			{
				name: 'atoRequest.TQi_Identity',
				label: 'Request ID',
				dataType: { name: DataTypeEnum.Long },
			},
			{
				name: 'atoRequest',
				label: 'Request Type',
				dataType: { name: DataTypeEnum.String },
				render: this.formatType,
				args: [this],
				filter: createFilterModel({
					not: true,
					operation: {
						value: FilterType.IN,
						displayName: 'filter.option.is-in',
						dataTypeNames: [DataTypeEnum.String],
					},
					value: ['MAAS-SUBMIT-FOUNDATION'],
				}),
			},
			{
				name: 'requestStatus',
				label: 'Request Status',
				dataType: { name: DataTypeEnum.String },
				render: this.formatStatus,
				args: [this],
			},
			{
				name: 'atoRequest.TQk_ATO_Received',
				label: 'ATO Received',
				dataType: { name: DataTypeEnum.Timestamp },
				/*
				 NOTE: `format` is required as the table column format doesn't match with the filter format.
				 */
				format: LocalizedDateTimeFormats.DateTime,
				dateTimeProperties: {
					format: LocalizedDateTimeFormats.Date,
				},
			},
			{
				name: 'atoResponse.TOz_Result',
				label: 'Response Result',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: 'atoResponse.TOz_Data_Exists',
				label: 'Action Indicator',
				dataType: { name: DataTypeEnum.String },
			},
			{
				name: 'tqn_BatchID',
				label: 'Batch ID',
				dataType: { name: DataTypeEnum.Long },
			},
			{
				name: 'transactionHistory.THl_Trans_Ref',
				label: 'Transaction Ref',
				dataType: { name: DataTypeEnum.Long },
				hiddenFrom: [HiddenTarget.TABLE],
			},
		];
	}

	protected activate(
		params: any,
		routeConfig: RouteConfig,
		navigationInstruction: NavigationInstruction,
	): Promise<any> {
		this.canRefresh = false;

		this.fund = params[CommonURLParam.FUND];
		this.member = params[CommonURLParam.MEMBER];
		this.division = params[CommonURLParam.DIVISION];
		this.unfilteredFund = this.fund;
		this.unfilteredMember = this.member;

		GenericApi.formName =
			CustomisationsApi.getFormNameOnActivate(routeConfig);
		GenericApi.customisationName =
			CustomisationsApi.getCustomisationNameOnActivate(params);

		// start the api request as early as we can, we will process it on form load
		this.initialData = new Promise<any>((resolve, reject) => {
			if (
				(!this.fund || (this.fund && this.fund.trim().length === 0)) &&
				this.member &&
				this.member.trim().length > 0
			) {
				reject(
					this.i18n.tr('errors.must-select-fund-if-member-selected'),
				);
			} else if (this.division && this.division.trim().length !== 1) {
				reject(this.i18n.tr('errors.division-invalid'));
			} else {
				resolve(this.getATOInterfacesData());
			}
		});

		return Promise.all([
			super.activate(params, routeConfig, navigationInstruction),
			this.customisationsApi
				.getCustomisationOnActivate(params, routeConfig)
				.then((customisationConfig) => {
					this.customisationConfig = customisationConfig;
					const atoInterfacesConfigWrapper =
						customisationConfig.functionConfig as any as AtoInterfacesConfigWrapper;
					this.atoInterfacesConfig =
						atoInterfacesConfigWrapper.atoInterfacesConfig;

					const model: ArchiveCheckboxModel =
						getArchiveCheckboxModel(customisationConfig);
					this.archiveCheckboxHidden = model.archiveCheckboxHidden;
					this.archiveCheckboxChecked = model.archiveCheckboxChecked;
				}),
			this.initialData,
		]).catch((err) => {
			// NOTE: Jake: 2019-06-13 - WEBUI-2133
			// Catching the error here is required otherwise reject()
			// errors are ignored. Bubbles up and is caught by something
			// else that shows a Notification.
		});
	}

	private readonly onClickRecord = (data): void => {
		BlockUtils.toggleAppInput(true);
		this.api
			.getAtoInterfacesRequestResponse(data.DT_RowId)
			.then((atoInterfaceModel) => {
				this.openDrilldownDialog(atoInterfaceModel);
			})
			.catch((error) => {
				this.handleWebError('errors.failed-to-load-record-data', error);
			});
	};

	public attached() {
		if (!this.atoInterfaceTable) {
			throw new Error('Missing atoInterfaceTable');
		}
		this.atoInterfaceTable.setListeners(this.onClickRecord);
		this.disableClickableElements();
		this.initialData
			.then((model) => {
				this.atoInterfacesData = model;
				if (!this.atoInterfaceTable) {
					throw new Error('Missing atoInterfaceTable');
				}

				try {
					// cannot reliably translate descriptions from i18n because codelist is modified after ajax table data has already loaded
					const requestTypeCodelist: CodeAndDescription[] = [
						{
							code: ATORequestType.STIC,
							description: 'Super TFN Integrity Check',
						},
						{
							code: ATORequestType.SMAT,
							description: 'SuperMatch',
						},
						{ code: ATORequestType.MATS, description: 'TBAR/MATS' },
						{
							code: ATORequestType.MAAS_GET,
							description: 'Member Detail Enquiry',
						},
						{
							code: ATORequestType.MAAS_SUBMIT,
							description: 'Member Registration/Update',
						},
						{
							code: ATORequestType.MAAS_SUBMIT_FOUNDATION,
							description: 'Member Foundation',
						},
						{
							code: ATORequestType.SMSFVRFY,
							description: 'SMSF Verification',
						},
					];
					this.atoInterfaceTable.tableColumns.find(
						(column) => column.name === 'atoRequest',
					).codeList = requestTypeCodelist;
				} catch (error) {
					Logger.error(error);
				}

				this.atoInterfaceTable.initAjaxTable(
					this.api.getATOInterfacesTableDataApiUrl(
						this.fund,
						this.member,
						this.division,
						this.queryParams,
					),
					AtoInterfacesIcons,
				);
				this.atoInterfaceTable.onAjaxLoaded().then(() => {
					this.enableClickableElements();
				});

				JQueryHelper.whenReady('[data-toggle="tooltip"]').then((obj) =>
					obj.tooltip({ delay: { show: 500, hide: 100 } }),
				);

				$('#' + this.atoInterfaceTable.tableId)
					.DataTable()
					.on('draw.dt', () => {
						this.addAnchorListeners();
					});
			})
			.catch((errorLoadingData) => {
				Notification.error(errorLoadingData);
			});
	}

	public unbind() {
		this.eventSubscriptions.forEach((subscription) => {
			subscription.dispose();
		});
	}

	/**
	 * Handle notifications from child elements
	 */
	public initEventHandlers() {
		this.subscribeToEvent(Filter.Events.RefreshFilter, (data) => {
			if (data.source === this.atoInterfaceFilter.nodeId) {
				this.refreshRequestControls();
			}
		});
	}

	@computedFrom(
		// NOTE: Jake: 2019-08-12 - WEBUI-2182
		// Not using "recordSelectorTable.selectedObj" as that causes the Chrome to hang
		// and not load. Can't debug anything because Chrome just dies with no error messages.
		// Since "selectedObj" is updated based on "selectedObjId", this alternative method works fine.
		'atoInterfaceTable.selectedObjId',
	)
	protected get contextMenuItems(): ContextMenuItem[] {
		if (!this.atoInterfaceTable) {
			return [];
		}
		const recordId = Number(this.atoInterfaceTable.selectedObjId);
		const record = this.atoInterfaceTable.selectedObj as ATOInterfacesModel;

		// Calculate context menu states
		let releaseOnHoldDisabled = false;
		let markReviewedHidden = false;
		let responseReviewedHidden = false;
		let responseReviewedDisabled = false;
		let resubmitDisabled = false;
		let resubmitBatchDisabled = false;
		if (record) {
			const requestStatusCode = record.atoRequest.TQz_Status
				? record.atoRequest.TQz_Status.trim()
				: '';
			const requestType = record.atoRequest.TQv_Type;
			if (requestStatusCode !== 'O' || !record.releaseOnHoldEnabled) {
				releaseOnHoldDisabled = true;
			}
			markReviewedHidden = true;
			responseReviewedHidden = true;

			// this menu item only serves as indicator, can always be disabled
			responseReviewedDisabled = true;
			resubmitDisabled = !this.isResubmitEnabled(
				requestType,
				record.requestStatus,
			);
			resubmitBatchDisabled = !this.isResubmitBatchEnabled(
				requestType,
				requestStatusCode,
				record.tqn_BatchID,
			);

			// Generic Type would transform 38 MATS-XXX type into generic ATORequestType.MATS
			let requestTypeGeneric: string = requestType;
			if (requestType.startsWith(ATORequestType.MATS)) {
				requestTypeGeneric = ATORequestType.MATS;
			}

			switch (requestTypeGeneric) {
				case ATORequestType.MATS:
				case ATORequestType.MAAS_GET:
				case ATORequestType.MAAS_SUBMIT:
				case ATORequestType.MAAS_SUBMIT_FOUNDATION:
				case ATORequestType.SMSFVRFY:
					if (!record.atoResponse) {
						break;
					}
					const dataExists = record.atoResponse.TOz_Data_Exists
						? record.atoResponse.TOz_Data_Exists.trim()
						: '';
					switch (dataExists) {
						case 'A':
						case 'E':
							markReviewedHidden = false;
							break;
						case 'R':
							responseReviewedHidden = false;
							break;
					}
					break;
			}
		}

		const result: ContextMenuItem[] = [
			{
				id: this.releaseOnHoldMenuId,
				label: this.i18n.tr(
					'ato-interfaces:release-one-on-hold-request',
				),
				iconClass: 'fa-step-forward',
				onClick: () => this.releaseOneOnHoldRequest(),
				disabled: releaseOnHoldDisabled,
			},
		];
		if (!markReviewedHidden) {
			result.push({
				id: this.markReviewedMenuId,
				label: this.i18n.tr('ato-interfaces:mark-response-as-reviewed'),
				iconClass: 'fa-check',
				onClick: () => this.showResponseReviewedDialog(),
			});
		}
		if (!responseReviewedHidden) {
			result.push({
				id: this.responseReviewedMenuId,
				label: this.i18n.tr(
					'ato-interfaces:response-has-been-reviewed',
				),
				iconClass: 'fa-exclamation',
				disabled: responseReviewedDisabled,
			});
		}
		result.push({
			id: this.resubmitMenuId,
			label: this.i18n.tr('ato-interfaces:resubmit-request'),
			iconClass: 'fa-step-forward',
			onClick: () => this.resubmitRequest(),
			disabled: resubmitDisabled,
		});
		result.push({
			id: this.resubmitBatchMenuId,
			label: this.i18n.tr('ato-interfaces:resubmit-batch'),
			iconClass: 'fa-step-forward',
			onClick: () => this.resubmitBatch(record.tqn_BatchID),
			disabled: resubmitBatchDisabled,
		});
		return result;
	}

	private readonly isResubmitEnabled = (
		requestType: string,
		requestStatus: RequestStatus,
	): boolean => {
		return (
			(requestType.startsWith('MAAS-') ||
				requestType.startsWith('MATS-')) &&
			this.errorRequestStatuses.indexOf(requestStatus.status) > -1 &&
			(!requestStatus.requestType ||
				(requestStatus.requestType === RequestType.ORIGINAL &&
					requestStatus.linkedRequestId > 0))
		);
	};

	private readonly isResubmitBatchEnabled = (
		requestType: string,
		requestStatusCode: string,
		batchId: string,
	): boolean => {
		return (
			(requestType.startsWith('MAAS-') ||
				requestType.startsWith('MATS-')) &&
			requestStatusCode === 'E' &&
			Number(batchId) > 0
		);
	};

	private readonly formatType = (
		classRef: ATOInterfaces,
		request: ATORequest,
	): string => {
		const requestType = request ? request.TQv_Type.escapeHtml() : '';
		if (!classRef.atoInterfacesData) {
			Logger.error(
				'Unable to format request type. atoInterfacesData is undefined.',
			);
			return requestType;
		}
		switch (requestType) {
			case ATORequestType.STIC:
				const sticFieldModel: ColumnValue<string> = {
					...classRef.atoInterfacesData.tableColumnDefinitions
						.TQz_STIC_Mode,
					value: request.TQz_STIC_Mode,
				};
				return (
					ATORequestType.STIC +
					' (' +
					Converters.columnCodeToDescription(
						<FieldModel>sticFieldModel,
						PresentationType.DESCRIPTION,
					) +
					')'
				);
			case ATORequestType.SMAT:
				return ATORequestType.SMAT;
			case ATORequestType.MAAS_SUBMIT:
				const maasSticMode: ColumnValue<string> = {
					...classRef.atoInterfacesData.tableColumnDefinitions
						.TQz_STIC_Mode,
					value: request.TQz_STIC_Mode,
				};
				if (maasSticMode.value === 'R') {
					return requestType + ' (Registration)';
				} else {
					return requestType + ' (Update)';
				}
			default:
				if (
					requestType.startsWith('MATS-') ||
					requestType.startsWith('MAAS-') ||
					requestType === ATORequestType.SMSFVRFY
				) {
					return requestType;
				}
				return '';
		}
	};

	private readonly formatStatus = (
		classRef: ATOInterfaces,
		requestStatus: RequestStatus,
		columnInfo: ColumnDefinition,
	): string => {
		let statusValue: string = '';
		if (requestStatus.status) {
			statusValue = requestStatus.status.escapeHtml();
		}
		if (columnInfo.codeList) {
			for (const code of columnInfo.codeList) {
				if (code.code === statusValue.trim()) {
					statusValue += ' (' + code.description.escapeHtml() + ')';
					break;
				}
			}
		}

		if (requestStatus.requestType && requestStatus.linkedRequestId) {
			const anchorClass =
				requestStatus.requestType === RequestType.ORIGINAL
					? 'cancellation'
					: 'original';
			const anchorText =
				requestStatus.requestType === RequestType.ORIGINAL
					? 'cancellation-request'
					: 'original-request';
			statusValue +=
				'<br/><a href="javascript:void(0)" class="request-anchor ' +
				anchorClass +
				'" id="' +
				requestStatus.linkedRequestId +
				'">' +
				classRef.i18n.tr('ato-interfaces:' + anchorText) +
				'</a>';
		}
		return statusValue;
	};

	private addAnchorListeners() {
		$('#' + this.atoInterfaceTable.tableId)
			.find('.request-anchor')
			.on('click', (event) => {
				event.stopPropagation();
				const $anchor = $(event.currentTarget);
				let getRequestResponse: Promise<any>;

				BlockUtils.toggleAppInput(true);
				if ($anchor.hasClass('original')) {
					getRequestResponse =
						this.api.getAtoInterfacesRequestResponse(
							Number($anchor.attr('id')),
						);
				} else if ($anchor.hasClass('cancellation')) {
					getRequestResponse =
						this.api.getCancellationRequestResponse(
							Number($anchor.attr('id')),
						);
				}

				getRequestResponse
					.then((model) => {
						this.openDrilldownDialog(model);
					})
					.catch((error) => {
						this.handleWebError(
							'errors.failed-to-load-record-data',
							error,
						);
					});
			});
	}

	public openDrilldownDialog(model: ATOInterfacesModel) {
		const matchedFundsLoaded = this.fetchMatchedFundsAsync(
			model.responseBean,
			model.atoRequest.TQz_Client,
		);
		const tableColumnDefinitions =
			this.atoInterfacesData.tableColumnDefinitions;

		const drilldownHeaderCommonModel = new DrilldownCommonModel(
			PLATFORM.moduleName(
				'forms/ato-interfaces/drilldown-dialog/drilldown-dialog-header.html',
			),
			model,
			tableColumnDefinitions,
			this.isArchiveRecord(model),
		);
		const tabList = TemplateMapping.getAsListOfNewTabs(
			model,
			tableColumnDefinitions,
			this,
		);
		const dialogWithTabsModel: DialogWithTabsModel = {
			recordId: String(model.atoRequest.TQi_Identity),
			drilldownModel: drilldownHeaderCommonModel,
			tabTemplates: tabList,
			parentTable: this.atoInterfaceTable,
			dialogSize: DialogSize.LARGE,
		};

		this.dialogUtils
			.showCustomDialog(DialogWithTabs, dialogWithTabsModel)
			.whenClosed(() => {
				this.publishEvent(BaseDialog.Events.CloseBaseDialog, {
					source: ATOInterfaces.FORM_NAME,
				});
			});
		BlockUtils.toggleAppInput(false);

		if (model.invalidReasons && model.invalidReasons.length > 0) {
			Notification.warning(model.invalidReasons.join('<br/>'));
		} else if (model.inError && model.responseBean.inError) {
			// if any part of response bean is in error, still open drilldown but display error
			Notification.error(
				this.i18n.tr('ato-interfaces:error-parsing-raw-data'),
			);
		}
	}

	public requestNewStic3() {
		if (
			!this.canRefresh ||
			!this.atoInterfacesData.requestControls.requestSticValidation
				.enable ||
			!this.atoInterfacesConfig.requestSticValidation.enabled
		) {
			return;
		}
		BlockUtils.toggleAppInput(true);
		this.api
			.requestNewStic3(this.member, this.fund)
			.then(() => {
				this.refreshTable();
				Notification.success(
					this.i18n.tr('ato-interfaces:success-stic-3'),
				);
			})
			.catch((error) => {
				this.handleWebError(
					'ato-interfaces:failed-to-request-stic-3',
					error,
				);
			});
	}

	private requestRegistrationStic3(): void {
		if (
			!this.canRefresh ||
			!this.atoInterfacesData.requestControls.requestSticRegistration
				.enable ||
			!this.atoInterfacesConfig.requestSticRegistration.enabled
		) {
			return;
		}
		BlockUtils.toggleAppInput(true);
		this.api
			.requestRegistrationStic3(this.member, this.fund)
			.then(() => {
				this.refreshTable();
				Notification.success(
					this.i18n.tr('ato-interfaces:success-registration-stic'),
				);
			})
			.catch((error) => {
				this.handleWebError(
					'ato-interfaces:failed-to-request-registration-stic',
					error,
				);
			});
	}

	public requestClosedAccountStic3() {
		if (
			!this.canRefresh ||
			!this.atoInterfacesData.requestControls.requestSticClosedAccount
				.enable ||
			!this.atoInterfacesConfig.requestSticClosedAccount.enabled
		) {
			return;
		}
		BlockUtils.toggleAppInput(true);
		this.api
			.requestClosedAccountStic3(this.member, this.fund)
			.then(() => {
				this.refreshTable();
				Notification.success(
					this.i18n.tr('ato-interfaces:success-closed-account-stic'),
				);
			})
			.catch((error) => {
				this.handleWebError(
					'ato-interfaces:failed-to-request-closed-account-stic',
					error,
				);
			});
	}

	public requestNewSmat2() {
		if (
			!this.canRefresh ||
			!this.atoInterfacesData.requestControls.requestSmat.enable ||
			!this.atoInterfacesConfig.requestSmat.enabled
		) {
			return;
		}
		BlockUtils.toggleAppInput(true);
		this.api
			.requestNewSmat2(this.member, this.fund)
			.then(() => {
				this.refreshTable();
				Notification.success(
					this.i18n.tr('ato-interfaces:success-smat-2'),
				);
			})
			.catch((error) => {
				this.handleWebError(
					'ato-interfaces:failed-to-request-smat-2',
					error,
				);
			});
	}

	public requestMaasGet(reason: string) {
		BlockUtils.toggleAppInput(true);
		this.api
			.requestMaasGet(this.member, this.fund, reason)
			.then(() => {
				this.refreshTable();
				Notification.success(
					this.i18n.tr('ato-interfaces:success-maas-get'),
				);
			})
			.catch((error) => {
				this.handleWebError(
					'ato-interfaces:failed-to-request-maas-get',
					error,
				);
			});
	}

	public requestMaasSubmitRegistration() {
		if (
			!this.canRefresh ||
			!this.atoInterfacesData.requestControls
				.requestMaasSubmitRegistration.enable ||
			!this.atoInterfacesConfig.requestMaasSubmitRegistration.enabled
		) {
			return;
		}
		BlockUtils.toggleAppInput(true);
		this.api
			.requestMaasSubmitRegistration(this.member, this.fund)
			.then(() => {
				this.refreshTable();
				Notification.success(
					this.i18n.tr(
						'ato-interfaces:success-maas-submit-registration',
					),
				);
			})
			.catch((error) => {
				this.handleWebError(
					'ato-interfaces:failed-to-request-maas-submit-registration',
					error,
				);
			});
	}

	public requestMaasSubmitUpdate() {
		if (
			!this.canRefresh ||
			!this.atoInterfacesData.requestControls.requestMaasSubmitUpdate
				.enable ||
			!this.atoInterfacesConfig.requestMaasSubmitUpdate.enabled
		) {
			return;
		}
		BlockUtils.toggleAppInput(true);
		this.api
			.requestMaasSubmitUpdate(this.member, this.fund)
			.then(() => {
				this.refreshTable();
				Notification.success(
					this.i18n.tr('ato-interfaces:success-maas-submit-update'),
				);
			})
			.catch((error) => {
				this.handleWebError(
					'ato-interfaces:failed-to-request-maas-submit-update',
					error,
				);
			});
	}

	public releaseFilteredOnHoldRequests() {
		if (
			!this.canRefresh ||
			!this.atoInterfacesData.requestControls.releaseOnHold.enable ||
			!this.atoInterfacesConfig.releaseOnHold.enabled
		) {
			return;
		}
		BlockUtils.toggleAppInput(true);
		const $table = $('#' + this.atoInterfaceTable.tableId);
		this.api
			.releaseFilteredOnHoldRequests(
				this.fund,
				this.member,
				this.division,
				$table.DataTable().ajax.params() as APIParameters,
			)
			.then(() => {
				this.refreshTable();
			})
			.catch((error) => {
				this.handleWebError(
					'ato-interfaces:failed-to-release-maas-request',
					error,
				);
			});
	}

	private getATOInterfacesData(
		requestControlFilters?: RequestControlFilters,
	): Promise<ATOInterfacesData> {
		return this.api
			.getATOInterfacesData(this.member, this.fund, requestControlFilters)
			.catch((error) => {
				// clear fund and member filter if error getting form data so previous fund/member set
				// through filter does not persist and allow certain request controls to be enabled
				if (
					!this.atoInterfaceTable ||
					!this.atoInterfaceTable.tableColumnInfo
				) {
					return;
				}
				this.clearFundMemberFilter();
				// disable for now. re-enable and retest when filters working
				// this.refreshRequestControls();
				this.handleWebError('errors.failed-to-load-form-data', error);
			});
	}

	private handleWebError(message, error) {
		Notification.error(
			this.i18n.tr('utils.message-type', {
				message: '$t(' + message + ')',
				type: FetchUtils.normalizeError(error),
			}),
		);

		this.canRefresh = true; // allow the user to recover from error by pressing refresh
		BlockUtils.toggleAppInput(false);
	}

	@computedFrom(
		'canRefresh',

		'atoInterfacesData.requestControls.requestSticRegistration.reason',
		'atoInterfacesData.requestControls.requestSticRegistration.enable',
		'atoInterfacesConfig.requestSticRegistration.enabled',

		'atoInterfacesData.requestControls.requestSticClosedAccount.reason',
		'atoInterfacesData.requestControls.requestSticClosedAccount.enable',
		'atoInterfacesConfig.requestSticClosedAccount.enabled',

		'atoInterfacesData.requestControls.requestMaasGet.reason',
		'atoInterfacesData.requestControls.requestMaasGet.enable',
		'atoInterfacesConfig.requestMaasGet.enabled',

		'atoInterfacesData.requestControls.requestMaasSubmitRegistration.reason',
		'atoInterfacesData.requestControls.requestMaasSubmitRegistration.enable',
		'atoInterfacesConfig.requestMaasSubmitRegistration.enabled',

		'atoInterfacesData.requestControls.requestMaasSubmitRegistration.reason',
		'atoInterfacesData.requestControls.requestMaasSubmitUpdate.enable',
		'atoInterfacesConfig.requestMaasSubmitUpdate.enabled',

		'atoInterfacesData.requestControls.exportToCsv.reason',
		'atoInterfacesData.requestControls.exportToCsv.enable',
		'atoInterfaceTable.recordsTotal',
	)
	private get atoInterfacesContextMenu(): ContextMenuItem[] {
		if (!this.atoInterfacesData) {
			return [];
		}
		const result: ContextMenuItem[] = [];
		if (!this.atoInterfacesData.maasEnabled) {
			result.push({
				label: this.i18n.tr('ato-interfaces:request-registration-stic'),
				onClick: () => this.requestRegistrationStic3(),
				tooltip:
					this.atoInterfacesData.requestControls
						.requestSticRegistration.reason,
				disabled:
					!this.canRefresh ||
					!this.atoInterfacesData.requestControls
						.requestSticRegistration.enable ||
					!this.atoInterfacesConfig.requestSticRegistration.enabled,
			});
			result.push({
				label: this.i18n.tr(
					'ato-interfaces:request-closed-account-stic',
				),
				onClick: () => this.requestClosedAccountStic3(),
				tooltip:
					this.atoInterfacesData.requestControls
						.requestSticClosedAccount.reason,
				disabled:
					!this.canRefresh ||
					!this.atoInterfacesData.requestControls
						.requestSticClosedAccount.enable ||
					!this.atoInterfacesConfig.requestSticClosedAccount.enabled,
			});
		} else {
			result.push({
				label: this.i18n.tr('ato-interfaces:request-maas-get'),
				onClick: () => this.showMaasGetDialog(),
				tooltip:
					this.atoInterfacesData.requestControls.requestMaasGet
						.reason,
				disabled:
					!this.canRefresh ||
					!this.atoInterfacesData.requestControls.requestMaasGet
						.enable ||
					!this.atoInterfacesConfig.requestMaasGet.enabled,
			});
			result.push({
				label: this.i18n.tr(
					'ato-interfaces:request-maas-submit-registration',
				),
				onClick: () => this.requestMaasSubmitRegistration(),
				tooltip:
					this.atoInterfacesData.requestControls
						.requestMaasSubmitRegistration.reason,
				disabled:
					!this.canRefresh ||
					!this.atoInterfacesData.requestControls
						.requestMaasSubmitRegistration.enable ||
					!this.atoInterfacesConfig.requestMaasSubmitRegistration
						.enabled,
			});
			result.push({
				label: this.i18n.tr(
					'ato-interfaces:request-maas-submit-update',
				),
				onClick: () => this.requestMaasSubmitUpdate(),
				tooltip:
					this.atoInterfacesData.requestControls
						.requestMaasSubmitUpdate.reason,
				disabled:
					!this.canRefresh ||
					!this.atoInterfacesData.requestControls
						.requestMaasSubmitUpdate.enable ||
					!this.atoInterfacesConfig.requestMaasSubmitUpdate.enabled,
			});
		}
		if (this.atoInterfaceTable.recordsTotal > 0) {
			result.push({
				label: this.i18n.tr('clickable-elements.export-to-excel'),
				onClick: () => this.exportToCSV(),
				tooltip:
					this.atoInterfacesData.requestControls.exportToCsv.reason,
				disabled:
					!this.canRefresh ||
					!this.atoInterfacesData.requestControls.exportToCsv.enable,
			});
		}
		return result;
	}

	public refresh() {
		if (!this.canRefresh) {
			return;
		}
		BlockUtils.toggleAppInput(true);
		this.refreshTable();
	}

	private refreshTable(resetPaging?: boolean) {
		this.atoInterfaceTable.refreshAjaxTable(resetPaging);
		BlockUtils.toggleAppInput(false);
	}

	/**
	 * Fetches the rollovers for each responseBean superfund in parallel.
	 */
	public fetchMatchedFundsAsync(
		responseBean: ResponseBean,
		clientID: string,
	) {
		if (!responseBean || !responseBean.superFunds || !clientID) {
			return new Promise((resolve, reject) => {
				resolve(); // no data to fetch
			});
		}

		const smat2RolloverRequests = new Array<Promise<any>>();
		for (const superFund of responseBean.superFunds) {
			smat2RolloverRequests.push(
				this.getSmat2RolloverMessages(superFund, clientID),
			);
		}

		return Promise.all(smat2RolloverRequests);
	}

	public getSmat2RolloverMessages(
		superFund: SuperFund,
		clientID: string,
	): Promise<any> {
		return this.api
			.getSmat2RolloverMessages(
				clientID,
				superFund.fundAbn,
				superFund.memberUsi,
			)
			.then((model) => {
				superFund.rolloverMessages = model;
				superFund.latestRollover = (<RolloverMessage[]>model)[0];
				superFund.latestRtr = (<RolloverMessage[]>model).find(
					(rolloverMessage, idx, arr) =>
						rolloverMessage.responseType.trim().toUpperCase() ===
						'RTR',
				);
			})
			.catch((error) =>
				this.handleWebError(
					'ato-interfaces:failed-to-retrieve-rollover-messages',
					error,
				),
			);
	}

	// NOTE(Jake): 2018-10-16 - WEBUI-689
	// Not used anymore seemingly. This function was
	// used to setup the shortcut key for the filter, however
	// I've simply moved the shortcut to be handled by `filter.ts` itself.
	/* protected openFilterSlideout() {
		this.atoInterfaceFilter.open();
	}

	protected closeFilterSlideout() {
		this.atoInterfaceFilter.close();
	}*/

	private releaseOneOnHoldRequest() {
		const requestId = parseInt(
			this.atoInterfaceTable.currentRowGetter().attr('id'),
			10,
		);
		BlockUtils.toggleAppInput(true);
		this.api
			.releaseOneOnHoldRequest(requestId)
			.then(() => {
				this.refreshTable();
			})
			.catch((error) => {
				this.handleWebError(
					'ato-interfaces:failed-to-release-maas-request',
					error,
				);
			});
	}

	private resubmitRequest() {
		const requestId = Number(
			this.atoInterfaceTable.currentRowGetter().attr('id'),
		);
		BlockUtils.toggleAppInput(true);
		this.api
			.resubmitRequest(requestId)
			.then(() => {
				this.refreshTable(false);
			})
			.catch((error) => {
				this.handleWebError(
					'ato-interfaces:failed-to-resubmit-request',
					error,
				);
			});
	}

	private resubmitBatch(batchId: string) {
		BlockUtils.toggleAppInput(true);
		this.api
			.resubmitBatch(batchId)
			.then(() => {
				this.refreshTable(false);
			})
			.catch((error) => {
				this.handleWebError(
					'ato-interfaces:failed-to-resubmit-batch',
					error,
				);
			});
	}

	private disableClickableElements() {
		this.atoInterfaceTable.toggleSpinner(true);
		$('[data-toggle="tooltip"]').tooltip('hide');
		this.canRefresh = false;
		this.atoInterfaceFilter.canClick = false;
	}

	private enableClickableElements() {
		this.atoInterfaceTable.toggleSpinner(false);
		this.canRefresh = true;
		this.atoInterfaceFilter.canClick = true;
	}

	private refreshRequestControls() {
		let fundFilter: FilterModel;
		let memberFilter: FilterModel;
		// certain filters are needed to determine whether a control is disabled
		const requestControlFilters: RequestControlFilters =
			new RequestControlFilters();
		this.atoInterfaceTable.tableColumnInfo.forEach((column) => {
			switch (column.name) {
				case 'atoRequest.TQz_Fund':
					fundFilter = column.filter;
					break;
				case 'atoRequest.TQz_Member':
					memberFilter = column.filter;
					break;
				case 'requestStatus':
				case 'atoRequest':
					requestControlFilters[column.name] = {
						data: column.name,
						value: column.filter.value
							? String(column.filter.value).trim()
							: '',
						operation: column.filter.operation.value,
						not: column.filter.not,
						ignoreCase: column.filter.ignoreCase,
					} as DataTableRequestFilter;
					break;
			}
		});

		if (!fundFilter || !memberFilter) {
			Logger.error(
				'Unable to refresh request controls. Column filters are undefined.',
			);
			return;
		}
		if (
			fundFilter.operation.value === FilterType.EQUALS &&
			String(fundFilter.value).trim() !== '' &&
			memberFilter.operation.value === FilterType.EQUALS &&
			String(memberFilter.value).trim() !== ''
		) {
			this.fund = String(fundFilter.value).trim().toUpperCase();
			this.member = String(memberFilter.value).trim().toUpperCase();
		} else {
			this.fund = this.unfilteredFund;
			this.member = this.unfilteredMember;
		}

		this.getATOInterfacesData(requestControlFilters).then((model) => {
			this.atoInterfacesData = model;
		});
	}

	private clearFundMemberFilter() {
		this.atoInterfaceTable.tableColumnInfo.forEach((column) => {
			if (
				column.name === 'atoRequest.TQz_Fund' ||
				column.name === 'atoRequest.TQz_Member'
			) {
				const columnName = column.name.replace('.', '-');
				if (!column.filter) {
					return;
				}
				// const inputId = '#filter-' + columnName;
				// $(inputId).val('');
				column.filter.value = '';
			}
		});
	}

	/**
	 * If this method is called with DataCompareTab parameter, it means this dialog is being displayed
	 * on top of DialogWithTabs and the parameter is used to disable and reinit this data compare tab
	 * @param dataCompareTab
	 */
	public showResponseReviewedDialog(dataCompareTab?: DataCompareTab) {
		this.dialogUtils.showDialog({
			simpleDialogType: SimpleDialogType.WARNING,
			bodyText: this.i18n.tr(
				'ato-interfaces:marking-as-reviewed-cannot-be-undone',
			),
			okFunction: () => this.markResponseAsReviewed(dataCompareTab),
		});
	}

	public markResponseAsReviewed(dataCompareTab: DataCompareTab) {
		const requestId = parseInt(
			this.atoInterfaceTable.currentRowGetter().attr('id'),
			10,
		);
		BlockUtils.toggleAppInput(true);
		if (dataCompareTab) {
			dataCompareTab.disableClickableElements();
		}

		this.api
			.markResponseAsReviewed(requestId)
			.then((model) => {
				this.refreshTable(false);
				if (dataCompareTab) {
					dataCompareTab.model = model;
					dataCompareTab.dataCompareTable.refreshNormalTable(
						model.comparedDataList,
					);
					dataCompareTab.reInitClickableElements();
					dataCompareTab.selectAllDisabled = true;
				}
			})
			.catch((error) => {
				this.handleWebError(
					'ato-interfaces:failed-to-update-response-data-exists',
					error,
				);
				if (dataCompareTab) {
					dataCompareTab.reInitClickableElements();
				}
			});
	}

	public showMaasGetDialog() {
		if (
			!this.canRefresh ||
			!this.atoInterfacesData.requestControls.requestMaasGet.enable ||
			!this.atoInterfacesConfig.requestMaasGet.enabled
		) {
			return;
		}

		const reasonFieldModel: FieldModel =
			this.atoInterfacesData.tableColumnDefinitions.TQz_Reason;
		reasonFieldModel.required = true;
		this.dialogUtils
			.showCustomDialog(MaasGetDialog, {
				fieldModel: reasonFieldModel,
			} as MaasGetDialogModel)
			.whenClosed((value) => {
				if (!value.wasCancelled) {
					const reason = (<MaasGetDialogModel>value.output).fieldModel
						.value;
					if (reason.trim() === '') {
						Notification.error(
							this.i18n.tr(
								'ato-interfaces:failed-to-request-maas-get',
								{
									reason: ': $t(ato-interfaces:request-reason-required)',
								},
							),
						);
					} else {
						this.requestMaasGet(reason);
					}
				}
			});
	}

	protected async exportToCSV(): Promise<void> {
		if (
			!this.canRefresh ||
			!this.atoInterfacesData.requestControls.exportToCsv.enable
		) {
			return;
		}

		this.disableClickableElements();
		const exportInstruction = { doExport: true, numberToExport: 0 };
		const closeResult = await this.dialogUtils
			.showCustomDialog(
				ExcelExportDialog,
				this.atoInterfaceTable.getExportDialogModel(),
			)
			.whenClosed((value) => value);
		exportInstruction.doExport = !closeResult.wasCancelled;
		if (exportInstruction.doExport) {
			exportInstruction.numberToExport = (
				closeResult.output as ExcelExportDialogModel
			).numberToExport;
			await this.exportAjaxTableRows(
				exportInstruction.numberToExport,
			).catch((error) => {
				Notification.error(
					this.i18n.tr('errors.failed-to-export-data-to-csv') +
						'. ' +
						error.error,
				);
				console.error(error);
			});
		}
		this.enableClickableElements();
	}

	protected async exportAjaxTableRows(numberToExport: number): Promise<void> {
		const dataGetter = (tableParams: APIParameters) =>
			this.api.getCSVData(
				this.atoInterfaceTable.validateNumberToExport(numberToExport),
				tableParams,
				this.fund,
				this.member,
				this.division,
			);

		await this.atoInterfaceTable.ajaxTableExport(
			ATOInterfaces.FORM_NAME,
			dataGetter,
		);
	}

	/*
	 * Called by Aurelia
	 */
	public archiveCheckboxCheckedChanged(oldVal: boolean, newVal: boolean) {
		setArchiveQueryParam(this.queryParams, this.archiveCheckboxChecked);
		if (this.atoInterfaceTable) {
			const ajaxUrl = this.api.getATOInterfacesTableDataApiUrl(
				this.fund,
				this.member,
				this.division,
				this.queryParams,
			);
			this.atoInterfaceTable.setAjaxURL(ajaxUrl);
		}

		this.refresh();
	}

	/**
	 * Called by acurity table when `draw.dt` event is triggered
	 */
	private onTableDraw = (
		acurityTable: AcurityTable,
		$table: JQuery<HTMLTableElement>,
	): void => {
		return customHighlight(
			acurityTable,
			$table,
			(record: AcurityTableRecord) => this.isArchiveRecord(record),
			'archive',
		);
	};

	private isArchiveRecord(
		record: AcurityTableRecord | ATOInterfacesModel,
	): boolean {
		const requestArchiveField = record.atoRequest?.TQn_RP_ID;
		const responseArchiveField = record.atoResponse?.TOn_RP_ID;
		return (
			(requestArchiveField && requestArchiveField !== 0) ||
			(responseArchiveField && responseArchiveField !== 0)
		);
	}
}
